import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Mail, Check, X, BookOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PendingInvitations({ onAccept }) {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      const response = await axios.get(`${API}/my-invitations`);
      setInvitations(response.data);
    } catch (error) {
      console.error('Failed to load invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (collaboratorId, status) => {
    setResponding(collaboratorId);
    try {
      await axios.put(
        `${API}/collaborators/${collaboratorId}`,
        { status },
        
      );
      
      if (status === 'accepted') {
        toast.success('You can now access this book!');
        if (onAccept) onAccept();
      } else {
        toast.info('Invitation declined');
      }
      
      setInvitations(invitations.filter(inv => inv.collaborator_id !== collaboratorId));
    } catch (error) {
      toast.error('Failed to respond to invitation');
    } finally {
      setResponding(null);
    }
  };

  if (loading) {
    return null;
  }

  if (invitations.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-[#8B7355]/10 to-[#A0522D]/10 dark:from-[#8B7355]/20 dark:to-[#A0522D]/20 rounded-2xl p-6 mb-8 border border-[#8B7355]/20">
      <div className="flex items-center space-x-3 mb-4">
        <Mail className="w-5 h-5 text-[#7C766E]" />
        <h3 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4]">
          Pending Invitations ({invitations.length})
        </h3>
      </div>
      
      <div className="space-y-3">
        {invitations.map((inv) => (
          <div
            key={inv.collaborator_id}
            className="flex items-center justify-between p-4 bg-white dark:bg-[#22201C] rounded-xl shadow-sm"
            data-testid={`invitation-${inv.collaborator_id}`}
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-lg bg-[#8B7355]/10 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-[#7C766E]" />
              </div>
              <div>
                <p className="font-medium text-[#2A2420] dark:text-[#F0EBE4]">
                  {inv.book_title}
                </p>
                <p className="text-sm text-[#2A2420]/60 dark:text-[#F0EBE4]/60">
                  {inv.inviter_name} invited you as {inv.role}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => handleRespond(inv.collaborator_id, 'accepted')}
                disabled={responding === inv.collaborator_id}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid={`accept-${inv.collaborator_id}`}
              >
                {responding === inv.collaborator_id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Accept
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => handleRespond(inv.collaborator_id, 'declined')}
                disabled={responding === inv.collaborator_id}
                className="border-red-300 text-red-600 hover:bg-red-50"
                data-testid={`decline-${inv.collaborator_id}`}
              >
                <X className="w-4 h-4 mr-1" />
                Decline
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PendingInvitations;
